import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusPill, type Status } from '../../design-system/primitives/StatusPill/StatusPill';
import { ArrowRightIcon } from '@phosphor-icons/react/dist/csr/ArrowRight';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowsClockwise';
import { GavelIcon } from '@phosphor-icons/react/dist/csr/Gavel';
import { CheckCircleIcon } from '@phosphor-icons/react/dist/csr/CheckCircle';
import { TimerIcon } from '@phosphor-icons/react/dist/csr/Timer';
import { TrayIcon } from '@phosphor-icons/react/dist/csr/Tray';
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning';

import { MetricCard, MetricGrid } from '../../design-system/composites/MetricCard/MetricCard';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import { ButtonLink } from '../../design-system/primitives/Button/ButtonLink';
import { writePath } from '../../app/routing';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { MfaEnrolmentScreen } from '../profile/MfaEnrolmentScreen';
import { ApiError, type ApiUser } from '../../lib/api/client';
import { casesApi, type CaseRow, type CaseStatus, type CaseSummary } from '../../lib/api/cases';
import { formatTimestamp } from '../../lib/format/dateTime';
import { canReadEscalations } from '../../lib/permissions/escalations';
import { canReadJustifications } from '../../lib/permissions/justifications';
import { canReadReplacements } from '../../lib/permissions/replacements';
import { hrefFor } from '../../app/routing';
import { mfaApi } from '../../lib/api/mfa';
import styles from './HomePage.module.css';

/**
 * The signed-in landing screen.
 *
 * ## It stopped being a design-system exercise (M-06 demo fixes)
 *
 * It was built as one, deliberately: the Phase 1 gate measured its six
 * checks — dark/light, comfortable/compact, Arabic RTL, no horizontal body
 * scroll, pinch zoom, axe — against real components rather than a
 * placeholder. That is why it carried a hard-coded `128` in its metric tile
 * and three invented cases in its table, with a five-state
 * `SegmentedControl` above them so a reviewer could step through every
 * `ListState`. The gate measured layout, not data.
 *
 * That was correct while there was nothing to read. It stopped being
 * correct the moment M-06 shipped a real case register: a fabricated KPI on
 * the first screen of a system whose whole claim is that it holds your real
 * cases is worse than showing no KPI at all. The tile now reads
 * `meta.total` from `GET /cases?status=open`, and the table below shows that
 * same query's newest rows.
 *
 * The state switcher went with them. The five states are still exercised —
 * by `DataTable`'s own suite, which owns them — and this screen now shows
 * whichever one its fetch actually produced.
 *
 * ## Two things it deliberately is not
 *
 * 1. **It renders no `<h1>`.** `AppShell` owns the application's single one
 *    (task 24 §11.1). Everything here is `<h2>` downwards, so the document
 *    outline is the shell's, not a competing one.
 *
 * 2. **The metric tile is not a new design-system primitive.** There is no
 *    `Card` in the design system — tasks 20-23 built ten primitives and
 *    three composites and none of them is one. Adding one here would mean a
 *    component with no contrast suite, no mutation pass and no second
 *    consumer, invented to satisfy one screen. It is a local composition of
 *    role tokens instead, measured by `modules.contrast.test.ts` like
 *    anything else, and the moment a second screen needs it that is the
 *    signal to promote it.
 *
 * The sign-out control lives here rather than in the topbar for a narrower
 * reason: adding a slot to `Topbar` means re-opening task 24's component and
 * its 37 Chromium specs to place a button, and the identity block is a
 * defensible home for "you are signed in as X — leave".
 */

/**
 * Enough rows to recognise the queue, not enough to be a second case list.
 * The Cases screen is one nav click away and owns filtering, paging and
 * search; duplicating any of that here would mean two screens to keep
 * honest instead of one.
 */
/**
 * How many open cases the dashboard holds.
 *
 * Was 5 — a table too short to be worth a control, which is why it had
 * none. It is the working set now: enough that sorting by age or searching
 * for a mobile number answers a real question, and still bounded, because
 * this screen is not the case register. `ClientDataTable`'s own docblock is
 * explicit that its search covers only rows already in the browser, which is
 * exactly why the note under the heading says which rows those are.
 */
const RECENT = 25;

/**
 * `CaseStatus` (the API's vocabulary) -> `StatusPill`'s `Status`. The two
 * differ in exactly one member — `in_progress` against `in-progress` — and
 * mapping the whole set rather than patching that one means a status added
 * to the API surfaces here as a TYPE ERROR rather than as a pill that
 * silently renders nothing.
 */
const PILL: Record<CaseStatus, Status> = {
  open: 'open',
  in_progress: 'in-progress',
  waiting: 'waiting',
  escalated: 'escalated',
  closed: 'closed',
  cancelled: 'cancelled',
};

export interface HomePageProps {
  user: ApiUser;
}

interface HomeCaseRow extends Row {
  case_number: string;
  mobile: string;
  status: CaseStatus;
  created_at: string | null;
}

/**
 * Every count in a `{status: n}` block.
 *
 * The blocks are OPEN MAPS — the server sends whatever statuses actually
 * occur, and a status with none is absent rather than zero — so a total has
 * to be summed rather than read from a known set of keys. A hard-coded list
 * would silently drop a status added later.
 */
/** Up to two initials for the welcome card's avatar ("Demo Administrator" -> "DA"). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toLocaleUpperCase();
}

function sum(counts: Readonly<Record<string, number>>): number {
  return Object.values(counts).reduce((running, one) => running + one, 0);
}

function toRow(one: CaseRow): HomeCaseRow {
  return {
    id: one.uuid,
    case_number: one.case_number,
    mobile: one.mobile,
    status: one.status,
    created_at: one.created_at,
  };
}

export function HomePage({ user }: HomePageProps) {
  const { t, i18n } = useTranslation();

  /**
   * Seeded from the resource and then owned locally.
   *
   * `user` is the object the sign-in returned and nothing refetches it, so an
   * enrolment completed on this screen would leave the panel still reading
   * "off" until the next reload. `useState`'s initialiser is not a subscription
   * — a later prop change would not reach it — and that is correct here: this
   * screen is the only thing that can change the value while it is mounted.
   */
  const [enrolled, setEnrolled] = useState(user.mfa_enabled);

  /**
   * A caller without `case.view` gets a `403` from the endpoint, so asking
   * at all would put an error panel where the honest answer is that this
   * section is not theirs. `App.tsx` guards the `/cases` route off the same
   * permission.
   */
  const canViewCases = user.permissions.includes('case.view');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly HomeCaseRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  /**
   * The headline figures, from `GET /cases/summary` (M-20).
   *
   * A SECOND REQUEST, and deliberately not folded into the list call above.
   * `casesApi.list({ status: 'open' })` can only ever answer how many OPEN
   * cases there are — it is one filtered page, and its `meta.total` counts
   * that filter. Escalated, closed and time-to-close are not derivable from
   * it at any price, and inventing them from the rows on screen would make
   * the dashboard disagree with `/reports` for the same account.
   *
   * `null` until it lands, and `null` FOREVER if it fails: the tiles then
   * read an em dash rather than a zero. A dashboard that reports "0
   * escalated" because a fetch failed is worse than one that admits it does
   * not know — that is the same rule the open-cases tile already followed
   * for its own count, applied to the other four.
   *
   * The failure is deliberately NOT surfaced as an error panel. This is a
   * supplementary row on a screen whose primary content (identity, MFA, the
   * recent list) is unaffected, and the recent list already owns the one
   * error state and support reference this screen shows.
   */
  const [summary, setSummary] = useState<CaseSummary | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');

    try {
      // ONE REQUEST FOR BOTH the tile and the table: `meta.total` counts
      // every open case the caller may see, and `data` is the newest RECENT
      // of exactly that set. A separate count call could answer a different
      // number than the rows sitting beside it.
      const page = await casesApi.list({ status: 'open', per_page: RECENT });

      setRows(page.data.map(toRow));
      setTotal(page.meta.total);
      setStatus(page.data.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      setReference(error.requestId ?? null);
      // CLEARED, NOT LEFT STANDING: a count from an earlier fetch displayed
      // beside an error panel is the same fabricated figure this screen just
      // stopped showing.
      //
      // DEFENSIVE, and honestly labelled as such: today the only refetch is
      // the retry inside the error state, which by definition has no count
      // to go stale, so no test can reach this line through the UI. It is
      // here for the refresh control or poll that eventually can.
      setTotal(null);
      setStatus('error');
    }

    /*
     * THE SUMMARY IS FETCHED OUTSIDE THE `try`, AND THAT PLACEMENT IS THE
     * WHOLE POINT.
     *
     * Inside it, a synchronous throw from this call — a mis-stubbed module,
     * a build that shipped without the method — lands in the `catch` above,
     * fails the `instanceof ApiError` test, and is rethrown out of an async
     * function nobody awaits. That is an unhandled rejection AND a list
     * that reports an error it did not have. Measured, not theorised: it is
     * what the first version of this did, fifteen times in one test file,
     * while the suite reported green.
     *
     * Out here it cannot touch the list's own state at all. `.catch()`
     * covers the async half and the whole expression is `void`-ed rather
     * than awaited: these tiles are supplementary, and making the table
     * wait on them would delay this screen's primary content for its
     * secondary one.
     *
     * `setSummary(null)` on failure, never a zeroed object — see the state's
     * own note. An em dash is honest; a zero is a claim.
     */
    try {
      void casesApi
        .summary()
        .then(setSummary)
        .catch(() => {
          setSummary(null);
        });
    } catch {
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    if (!canViewCases) return;
    void load();
  }, [canViewCases, load]);

  function renderStatus(row: Row): ReactNode {
    return <StatusPill status={PILL[(row as HomeCaseRow).status]} />;
  }

  function renderCreatedAt(row: Row): ReactNode {
    // `created_at` is nullable on the resource; DataTable renders its own
    // empty-cell treatment for null, which is why this returns null rather
    // than formatting the string "null".
    const value = (row as HomeCaseRow).created_at;

    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  const columns: readonly Column[] = [
    // `neverMirrors` on the first two: a case number and a mobile number are
    // code identifiers, and an Arabic page must not reverse their digits. A
    // date is prose and mirrors with the rest of the page, so it is not one.
    { key: 'case_number', label: t('table.caseNumber'), neverMirrors: true },
    { key: 'mobile', label: t('table.mobile'), neverMirrors: true },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'created_at', label: t('table.createdAt'), render: renderCreatedAt },
  ];

  return (
    <div className={styles['page']}>
      {/*
        THE TWO ACCOUNT PANELS, SIDE BY SIDE. They were stacked, and the
        second of them — two-step verification — is a panel most readers
        never touch after the day they enrol, so a full-width band of it sat
        between the reader and the figures they opened this screen for. They
        are the same KIND of thing (this is who you are signed in as, and
        this is how that account is protected), which is what makes them a
        pair rather than two sections that happen to be adjacent.

        The row wraps rather than shrinking: below `--breakpoint-md` two
        columns of this content are narrower than the prose in them.
      */}
      <div className={styles['account']}>
        <section className={styles['identity']} aria-labelledby="home-welcome">
          {/* Initials, decorative: the name beside it is the accessible text. */}
          <span className={styles['avatar']} aria-hidden="true">
            {initials(user.full_name)}
          </span>
          <div className={styles['identityText']}>
            <p className={styles['today']}>
              {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full' }).format(new Date())}
            </p>
            <h2 className={styles['welcome']} id="home-welcome">
              {t('home.welcome', { name: user.full_name })}
            </h2>
            <p className={styles['username']}>
              {t('home.identityLabel')} {/* A username is a code identifier: it never mirrors. */}
              <bdi dir="ltr">{user.username}</bdi>
            </p>
            <p className={styles['roles']}>
              <span className={styles['rolesLabel']}>{t('home.roles')}:</span>
              {user.roles.length === 0 ? (
                <span className={styles['role']}>{t('home.noRoles')}</span>
              ) : (
                user.roles.map((role) => (
                  <span key={role} className={styles['role']}>
                    {role}
                  </span>
                ))
              )}
            </p>
          </div>
        </section>

        {/*
        Two-step verification lives here rather than on a profile screen because
        there is no profile screen yet — M-01b builds one, and this section
        moves there whole when it does. Mounting it under the identity block is
        not a placeholder: "you are signed in as X" is exactly the context in
        which "and this is how that account is protected" belongs.
      */}
        <MfaEnrolmentScreen
          enabled={enrolled}
          onBegin={() => mfaApi.begin()}
          onConfirm={(code) => mfaApi.confirm(code)}
          onEnrolled={() => {
            // Local only. `mfa_required` on the user resource is the account's
            // POLICY, not its state, so there is nothing on it to refresh — the
            // fact that a factor now exists is what changed, and this is where
            // that is known.
            setEnrolled(true);
          }}
          onDisable={(password, code) => mfaApi.disable(password, code)}
          onRegenerate={(password) => mfaApi.regenerateRecoveryCodes(password)}
          onDisabled={() => {
            setEnrolled(false);
          }}
        />
      </div>

      {canViewCases ? (
        <>
          {/*
            THE FIVE FIGURES A SUPERVISOR OPENS THIS SCREEN FOR. It used to
            carry one — open cases — which is the number a person already
            knows from the list underneath it.

            An em dash for anything not yet known, never a `0`: a zero is a
            claim, and a failed summary fetch must not be able to report a
            quiet estate. `total` (from the list request) and the summary's
            own figures are tracked separately for that reason — one landing
            says nothing about the other.
          */}
          <MetricGrid>
            <MetricCard
              icon={<TrayIcon size={20} weight="duotone" />}
              label={t('home.openCases')}
              value={total === null ? '—' : String(total)}
              hint={t('home.openCasesHint')}
              testId="home-metric-open"
            />
            {/*
              NO "ESCALATED" STATUS TILE. It sat beside the "Escalations"
              tile below and read as the same figure twice; the escalation
              total is the one kept. The status breakdown is in Reports.
            */}
            <MetricCard
              icon={<ArrowsClockwiseIcon size={20} weight="duotone" />}
              label={t('home.metricInProgress')}
              value={summary === null ? '—' : String(summary.by_status.in_progress)}
              testId="home-metric-in-progress"
            />
            <MetricCard
              icon={<CheckCircleIcon size={20} weight="duotone" />}
              label={t('home.metricClosed')}
              value={summary === null ? '—' : String(summary.totals.closed)}
              testId="home-metric-closed"
            />
            <MetricCard
              icon={<TimerIcon size={20} weight="duotone" />}
              label={t('home.metricAverageDays')}
              value={
                summary === null || summary.totals.average_days_to_close === null
                  ? '—'
                  : t('reports.days', { count: summary.totals.average_days_to_close })
              }
              testId="home-metric-average-days"
            />

            {/*
              THE THREE CROSS-MODULE FIGURES, GATED ON THE CALLER'S
              PERMISSIONS — NOT ON WHETHER THE DATA HAS ARRIVED.
              ==========================================================
              A first cut rendered these only when `summary` already held
              their block, and the result was three cards popping into a
              grid that had been settled for a second: the five above them
              show an em dash from the first paint and fill in, and these
              did not exist until the response landed. Same grid, two
              different loading behaviours.

              Permissions come from `/me` and are in hand before the summary
              request is even sent, so they can decide the SHAPE of the grid
              immediately while the summary decides only the VALUES. The
              read helpers are the same ones the nav and each module's
              screen use, and the server now asks the same policies
              (`CaseSummaryController`), so "the card is here" and "the
              block will arrive" cannot disagree.

              `sum()` rather than one status: a dashboard answers "how much
              is there", and the breakdown is one click away in Reports.
              Replacements in particular count the WHOLE history — legacy's
              own live in that table now — so this is a real total rather
              than a figure that starts at zero on cutover day.
            */}
            {canReadReplacements(user.permissions) ? (
              <MetricCard
                icon={<ArrowsClockwiseIcon size={20} weight="duotone" />}
                label={t('home.metricReplacements')}
                value={
                  summary?.replacements === undefined
                    ? '—'
                    : String(sum(summary.replacements.by_status))
                }
                hint={t('home.metricReplacementsHint')}
                testId="home-metric-replacements"
              />
            ) : null}
            {canReadEscalations(user.permissions) ? (
              <MetricCard
                icon={<WarningIcon size={20} weight="duotone" />}
                label={t('home.metricEscalations')}
                value={
                  summary?.escalations === undefined
                    ? '—'
                    : String(sum(summary.escalations.by_status))
                }
                hint={t('home.metricEscalationsHint')}
                testId="home-metric-escalations"
              />
            ) : null}
            {canReadJustifications(user.permissions) ? (
              <MetricCard
                icon={<GavelIcon size={20} weight="duotone" />}
                label={t('home.metricComplaints')}
                value={
                  summary?.justifications === undefined
                    ? '—'
                    : String(sum(summary.justifications.by_status))
                }
                hint={t('home.metricComplaintsHint')}
                testId="home-metric-complaints"
              />
            ) : null}
          </MetricGrid>

          <section className={styles['list']}>
            {/*
              WHICH ROWS THESE ARE, said out loud. The search box below
              covers the rows in the browser and nothing else — the endpoint
              has no `q` — so a reader who typed a case number into it and
              got nothing would conclude the case does not exist. The note
              is what makes that search honest, and the link is where the
              question it could not answer actually gets answered.
            */}
            <ClientDataTable
              /* The scope note sits INSIDE the table's card, on its toolbar:
                 on the page ground it was 12px caption text over the
                 background image, and it cost a row of its own. */
              toolbarActions={
                <>
                  <p className={styles['scope']}>{t('home.recentScope', { count: RECENT })}</p>
                  {/* A command, so drawn as one — not underlined text. */}
                  <ButtonLink
                    href={hrefFor('/cases')}
                    variant="ghost"
                    icon={<ArrowRightIcon size={16} className={styles['forwardIcon']} />}
                  >
                    {t('home.allCases')}
                  </ButtonLink>
                </>
              }
              caption={t('table.caption')}
              columns={columns}
              rows={rows}
              status={status}
              /* The row id is the case uuid (`toRow`), and `#/cases/{uuid}`
                 opens the register with that case already showing — the same
                 detail the case list opens, reached the same way. Before
                 this, clicking a row here did nothing. */
              onRowActivate={(row) => {
                writePath(`/cases/${row.id}`);
              }}
              onRetry={() => {
                void load();
              }}
            />

            {/*
              The support reference below the table rather than inside it:
              `ErrorCopy` carries a title and a cause and no field for one,
              which is `CaseList`'s own arrangement for the identical value.
            */}
            {status === 'error' && reference !== null ? (
              <p className={styles['reference']}>
                {t('auth.supportReference')}{' '}
                <bdi dir="ltr" data-testid="homeRequestId">
                  {reference}
                </bdi>
              </p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
